import React, { useState, useEffect } from 'react';

const ParcoursMapNeon = ({ 
  activeNode = 'web3', 
  reduceMotion = false, 
  onRocketLaunch,
  onNodeClick,
  progress = null,
  lang = 'es'
}) => {
  const [particleProgress, setParticleProgress] = useState(0);
  const [rocketLaunched, setRocketLaunched] = useState(false);

  // Translations
  const labels = {
    en: { web3: 'Web3', ai: 'AI Trading', dca: 'DCA', castle: 'Goal', start: 'Start' },
    es: { web3: 'Web3', ai: 'Trading IA', dca: 'DCA', castle: 'Meta', start: 'Inicio' },
    fr: { web3: 'Web3', ai: 'Trading IA', dca: 'DCA', castle: 'Objectif', start: 'Départ' }
  };

  const t = labels[lang] || labels.es;

  // Particle animation along the path
  useEffect(() => {
    if (reduceMotion) return;
    
    const interval = setInterval(() => {
      setParticleProgress(prev => (prev >= 100 ? 0 : prev + 0.5));
    }, 50);
    
    return () => clearInterval(interval);
  }, [reduceMotion]);

  // Node positions (percentage along path)
  const nodes = {
    start: { x: 8, y: 80, label: t.start, emoji: '🏁', size: 'tiny' },
    web3: { x: 22, y: 65, label: t.web3, emoji: '🌐', size: 'small' },
    ai: { x: 50, y: 40, label: t.ai, emoji: '🤖', size: 'medium' },
    dca: { x: 75, y: 55, label: t.dca, emoji: '📈', size: 'large' },
    castle: { x: 92, y: 25, label: t.castle, emoji: '🏆', size: 'final' }
  };

  // Check completed steps from progress
  const isNodeCompleted = (nodeKey) => {
    if (!progress?.completedSteps) return false;
    const stepMap = { web3: 'WEB3_1', ai: 'AI_1', dca: 'DCA_1' };
    return progress.completedSteps.includes(stepMap[nodeKey]);
  };

  // Get rocket position based on active node or progress
  const getRocketPosition = () => {
    // If rocket not launched yet, stay at start
    if (!rocketLaunched && !progress?.currentStep) {
      return { x: nodes.start.x, y: nodes.start.y - 10 };
    }
    
    // Map progress step to node
    if (progress?.currentStep) {
      const stepToNode = {
        'WEB3_1': 'web3',
        'AI_1': 'ai', 
        'DCA_1': 'dca'
      };
      const targetNode = stepToNode[progress.currentStep] || activeNode;
      const node = nodes[targetNode];
      return { x: node.x, y: node.y - 10 };
    }
    
    const node = nodes[activeNode];
    return { x: node.x, y: node.y - 10 };
  };

  const rocketPos = getRocketPosition();

  // Handle node click
  const handleNodeClick = (nodeKey) => {
    if (onNodeClick) onNodeClick(nodeKey);
  };

  // Launch rocket animation
  const launchRocket = () => {
    setRocketLaunched(true);
    if (onRocketLaunch) onRocketLaunch();
  };

  // Calculate particle position along curved path
  const getParticlePosition = (progress) => {
    const t = progress / 100;
    // Cubic bezier curve points
    const points = [
      { x: 10, y: 75 },
      { x: 40, y: 40 },
      { x: 70, y: 65 },
      { x: 95, y: 30 }
    ];
    
    // Cubic bezier interpolation
    const x = Math.pow(1-t, 3) * points[0].x + 
              3 * Math.pow(1-t, 2) * t * points[1].x + 
              3 * (1-t) * Math.pow(t, 2) * points[2].x + 
              Math.pow(t, 3) * points[3].x;
    
    const y = Math.pow(1-t, 3) * points[0].y + 
              3 * Math.pow(1-t, 2) * t * points[1].y + 
              3 * (1-t) * Math.pow(t, 2) * points[2].y + 
              Math.pow(t, 3) * points[3].y;
    
    return { x, y };
  };

  const particlePos = getParticlePosition(particleProgress);

  return (
    <div className="w-full h-[500px] md:h-[500px] sm:h-[320px] relative overflow-hidden rounded-t-2xl">
      <svg 
        width="100%" 
        height="100%" 
        viewBox="0 0 100 100" 
        preserveAspectRatio="xMidYMid meet"
        className="absolute inset-0"
      >
        {/* Definitions for glows and gradients */}
        <defs>
          {/* Glow filter for route */}
          <filter id="routeGlow">
            <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          
          {/* Pulse glow filter */}
          <filter id="pulseGlow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          
          {/* Gradient for route */}
          <linearGradient id="routeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#00f4ff" stopOpacity="0.6" />
            <stop offset="50%" stopColor="#12b7ff" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#00f4ff" stopOpacity="0.6" />
          </linearGradient>
        </defs>

        {/* Background Grid */}
        <defs>
          <pattern id="smallGrid" width="5" height="5" patternUnits="userSpaceOnUse">
            <path d="M 5 0 L 0 0 0 5" fill="none" stroke="#00f4ff" strokeWidth="0.05" opacity="0.3"/>
          </pattern>
        </defs>
        <rect width="100" height="100" fill="url(#smallGrid)" opacity="0.3"/>

        {/* Curved Route Path - Updated to include start node */}
        <path
          d="M 8 80 Q 15 72, 22 65 Q 35 50, 50 40 Q 62 48, 75 55 Q 83 40, 92 25"
          fill="none"
          stroke="url(#routeGradient)"
          strokeWidth="0.8"
          strokeDasharray="2,2"
          filter="url(#routeGlow)"
          opacity="0.4"
        />

        {/* Thicker glow base for route */}
        <path
          d="M 8 80 Q 15 72, 22 65 Q 35 50, 50 40 Q 62 48, 75 55 Q 83 40, 92 25"
          fill="none"
          stroke="#00f4ff"
          strokeWidth="0.3"
          opacity="0.2"
          filter="url(#routeGlow)"
        />

        {/* Moving Particle */}
        {!reduceMotion && (
          <circle
            cx={particlePos.x}
            cy={particlePos.y}
            r="0.6"
            fill="#00f4ff"
            filter="url(#pulseGlow)"
            opacity="0.8"
          >
            <animate
              attributeName="opacity"
              values="0.3;1;0.3"
              dur="1.5s"
              repeatCount="indefinite"
            />
          </circle>
        )}

        {/* Nodes */}
        {Object.entries(nodes).map(([key, node]) => {
          const isActive = key === activeNode;
          const completed = isNodeCompleted(key);
          const sizeMap = { tiny: 4, small: 6, medium: 7, large: 8, final: 9 };
          const nodeSize = sizeMap[node.size];
          
          // Colors based on state
          const nodeColors = {
            active: { fill: 'rgba(0, 244, 255, 0.25)', stroke: '#00f4ff' },
            completed: { fill: 'rgba(16, 185, 129, 0.25)', stroke: '#10B981' },
            default: { fill: 'rgba(11, 28, 56, 0.8)', stroke: '#00f4ff' }
          };
          
          const colors = completed ? nodeColors.completed : (isActive ? nodeColors.active : nodeColors.default);
          
          return (
            <g 
              key={key} 
              className="cursor-pointer"
              onClick={() => handleNodeClick(key)}
              style={{ transition: 'transform 0.2s ease' }}
            >
              {/* Completed star indicator */}
              {completed && (
                <text
                  x={node.x + nodeSize + 1}
                  y={node.y - nodeSize + 1}
                  fontSize="4"
                >
                  ⭐
                </text>
              )}
              
              {/* Node glow (animated if active) */}
              <circle
                cx={node.x}
                cy={node.y}
                r={nodeSize + 2}
                fill="none"
                stroke={colors.stroke}
                strokeWidth="0.3"
                opacity={isActive ? "0.6" : "0.2"}
                filter="url(#pulseGlow)"
              >
                {isActive && !reduceMotion && (
                  <animate
                    attributeName="opacity"
                    values="0.2;0.8;0.2"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                )}
              </circle>
              
              {/* Outer pulse ring for active node */}
              {isActive && !reduceMotion && (
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={nodeSize + 4}
                  fill="none"
                  stroke="#00f4ff"
                  strokeWidth="0.2"
                  opacity="0.4"
                >
                  <animate
                    attributeName="r"
                    values={`${nodeSize + 2};${nodeSize + 6};${nodeSize + 2}`}
                    dur="2.5s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="opacity"
                    values="0.4;0.1;0.4"
                    dur="2.5s"
                    repeatCount="indefinite"
                  />
                </circle>
              )}
              
              {/* Node circle */}
              <circle
                cx={node.x}
                cy={node.y}
                r={nodeSize}
                fill={colors.fill}
                stroke={colors.stroke}
                strokeWidth={isActive ? "0.7" : "0.5"}
                filter="url(#routeGlow)"
              />
              
              {/* Node emoji/icon */}
              <text
                x={node.x}
                y={node.y + 1.5}
                textAnchor="middle"
                fontSize={nodeSize * 0.7}
                fill="white"
              >
                {node.emoji}
              </text>
              
              {/* Node label */}
              <text
                x={node.x}
                y={node.y + nodeSize + 4}
                textAnchor="middle"
                fontSize="3"
                fill={isActive ? '#00f4ff' : (completed ? '#10B981' : '#9EB4C0')}
                fontWeight={isActive ? "600" : "500"}
              >
                {node.label}
              </text>
            </g>
          );
        })}

        {/* Rocket (Player Token) */}
        <g 
          transform={`translate(${rocketPos.x}, ${rocketPos.y})`}
          style={{
            transition: 'transform 1s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          {/* Rocket glow */}
          <circle
            cx="0"
            cy="0"
            r="4"
            fill="#00f4ff"
            opacity="0.3"
            filter="url(#pulseGlow)"
          >
            {!reduceMotion && (
              <animate
                attributeName="opacity"
                values="0.2;0.5;0.2"
                dur="2s"
                repeatCount="indefinite"
              />
            )}
          </circle>
          
          {/* Rocket body */}
          <text
            x="0"
            y="1.5"
            textAnchor="middle"
            fontSize="8"
            fill="white"
            filter="url(#routeGlow)"
          >
            🚀
          </text>
          
          {/* Floating animation */}
          {!reduceMotion && (
            <animateTransform
              attributeName="transform"
              type="translate"
              values="0,0; 0,-2; 0,0"
              dur="3s"
              repeatCount="indefinite"
            />
          )}
        </g>
      </svg>
      
      {/* Optional: Stars/ambient particles in background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {!reduceMotion && [...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-[#00f4ff]"
            style={{
              left: `${10 + Math.random() * 80}%`,
              top: `${10 + Math.random() * 80}%`,
              width: `${2 + Math.random() * 3}px`,
              height: `${2 + Math.random() * 3}px`,
              opacity: 0.15,
              animation: `twinkle ${2 + Math.random() * 3}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`
            }}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="absolute bottom-2 left-2 flex items-center gap-3 text-[10px] sm:text-xs text-[#9EB4C0] bg-[#040816]/70 px-2 py-1 rounded-lg backdrop-blur-sm">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-[#00f4ff] animate-pulse"></span>
          {lang === 'en' ? 'Active' : (lang === 'fr' ? 'Actif' : 'Activo')}
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-[#10B981]"></span>
          {lang === 'en' ? 'Done' : (lang === 'fr' ? 'Fait' : 'Hecho')}
        </span>
      </div>
      
      {/* CSS for twinkle animation */}
      <style jsx>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
};

export default ParcoursMapNeon;
